import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Skeleton } from '../../../../components';
import type { AnalyticsHost } from '../../api';
import { pageName, viewsText } from '../../format';
import type { PageScope, Span } from '../../queries';
import type { TickState } from '../../selection';
import { viewsKey } from '../../useLandingPageViews';
import { usePageOptions, type PageOptionsQuality } from '../../usePageOptions';
import type { LandingOption, PickedPage } from './PagePicker';
import {
  SearchRow,
  SearchInput,
  StickySearch,
  Scroll,
  SectionLabel,
  Row,
  Thumb,
  RowText,
  RowName,
  RowSub,
  RowValue,
  Note,
  TickBox,
} from './PagePicker.styles';

export type PageListProps = {
  tenantId: number;
  /** The brand's addresses (the website's and its landing pages'). */
  hosts: AnalyticsHost[];
  /** The period the views are counted over. */
  span: Span;
  landingPages: LandingOption[];
  /** The pages picked so far (none: every page). */
  selected: readonly PageScope[];
  /** Several pages can be picked: each row's checkbox (ticked or empty, "All pages" ticked alone); with it, `selected`
   *  is not read. */
  tick?: (page: PageScope | null) => TickState;
  /** A page was ticked or unticked (null: "All pages", clearing them). */
  onPick: (page: PickedPage | null) => void;
  /** Puts the cursor in the search field as the list appears (the pill's panel; not in a phone's sheet, where it
   *  would raise the keyboard). */
  focusOnMount?: boolean;
  /** Pins the search field to the top while the pages scroll under it (the filters sheet, where the sheet scrolls the
   *  list; the pill's panel scrolls only the list). */
  stickySearch?: boolean;
  /** Quality mode: the pages and their views come from page discovery over the report's addresses and range. */
  quality?: PageOptionsQuality;
};

const addressOf = (url: string): string => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

/**
 * The pages to pick from: a search field, "All pages", the brand's landing pages (with their pictures) and the
 * website's most viewed pages (narrowed by the search on the analytics service). The page pill's panel shows it, and
 * so does the phone's filters sheet. Its views are read while it is shown.
 */
export const PageList = ({
  tenantId,
  hosts,
  span,
  landingPages,
  selected,
  tick,
  onPick: pick,
  focusOnMount = false,
  stickySearch = false,
  quality,
}: PageListProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [search, setSearch] = React.useState('');
  const input = React.useRef<HTMLInputElement>(null);
  const options = usePageOptions(tenantId, hosts, span, true, search, quality);

  React.useEffect(() => {
    if (focusOnMount) input.current?.focus();
  }, [focusOnMount]);

  const needle = search.trim().toLowerCase();
  const landing = landingPages
    .filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.path.toLowerCase().includes(needle))
    .map((p) => ({ ...p, views: options.landingViews.get(viewsKey(p.path)) }))
    .sort((a, b) => (b.views ?? 0) - (a.views ?? 0));
  const isActive = (kind: PickedPage['kind'], path: string): boolean =>
    tick ? tick({ kind, path }) === 'on' : selected.some((p) => p.kind === kind && p.path === path);
  const allActive = tick ? tick(null) === 'on' : selected.length === 0;
  const hasSite = hosts.some((h) => h.kind === 'site');
  // A row's checkbox where several pages can be picked (the row itself carries the pressed state).
  const box = (page: PageScope | null): React.ReactNode => {
    const state = tick?.(page);
    if (!state) return null;
    return (
      <TickBox $checked={state !== 'off'} aria-hidden="true">
        {state === 'on' ? <Icon name="check" size={14} /> : null}
        {state === 'mixed' ? <Icon name="minus" size={14} /> : null}
      </TickBox>
    );
  };

  const searchRow = (
    <SearchRow>
      <Icon name="search" size={16} />
      <SearchInput
        ref={input}
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('Search pages')}
        aria-label={t('Search pages')}
      />
    </SearchRow>
  );

  return (
    <>
      {stickySearch ? <StickySearch>{searchRow}</StickySearch> : searchRow}
      <Scroll>
        {!needle ? (
          <Row type="button" $active={!tick && allActive} aria-pressed={allActive} onClick={() => pick(null)}>
            {box(null)}
            <RowText>
              <RowName text="All pages" tag="span" />
              <RowSub text="Your website and landing pages together" tag="span" />
            </RowText>
          </Row>
        ) : null}

        {landing.length > 0 ? (
          <>
            <SectionLabel text="Landing pages" tag="span" />
            {landing.map((p) => (
              <Row
                key={p.path}
                type="button"
                $active={!tick && isActive('landing', p.path)}
                aria-pressed={isActive('landing', p.path)}
                onClick={() =>
                  pick({
                    kind: 'landing',
                    path: p.path,
                    name: p.name,
                    ...(p.landingPageId ? { landingPageId: p.landingPageId } : {}),
                  })
                }
              >
                {box({ kind: 'landing', path: p.path })}
                <Thumb aria-hidden="true">
                  {p.imageUrl ? <img src={p.imageUrl} alt="" loading="lazy" /> : <Icon name="document" size={18} />}
                </Thumb>
                <RowText>
                  <RowName text={p.name} tag="span" raw title={p.name} />
                  <RowSub text={addressOf(p.url)} tag="span" raw title={p.url} />
                </RowText>
                {p.views !== undefined ? <RowValue text={viewsText(p.views, t, language)} tag="span" raw /> : null}
              </Row>
            ))}
          </>
        ) : null}

        {hasSite ? (
          <>
            <SectionLabel text="Website pages" tag="span" />
            {options.loading && options.sitePages.length === 0 ? (
              Array.from({ length: 3 }, (_, i) => (
                <Row key={i} type="button" $active={false} disabled aria-hidden="true">
                  <RowText>
                    <Skeleton width="50%" height={14} radius={6} />
                    <Skeleton width="30%" height={12} radius={6} />
                  </RowText>
                </Row>
              ))
            ) : options.error ? (
              <Note text="We could not load your pages. Try again in a moment." tag="span" />
            ) : options.sitePages.length === 0 && needle ? (
              <Note text="No pages match your search." tag="span" />
            ) : options.sitePages.length === 0 ? (
              <Note text="No pages visited in this period yet." tag="span" />
            ) : (
              options.sitePages.map((p) => {
                const name = pageName(p.path, t);
                return (
                  <Row
                    key={p.path}
                    type="button"
                    $active={!tick && isActive('site', p.path)}
                    aria-pressed={isActive('site', p.path)}
                    onClick={() => pick({ kind: 'site', path: p.path, name })}
                  >
                    {box({ kind: 'site', path: p.path })}
                    <RowText>
                      <RowName text={name} tag="span" raw title={name} />
                      <RowSub text={p.path} tag="span" raw title={p.path} />
                    </RowText>
                    <RowValue text={viewsText(p.views, t, language)} tag="span" raw />
                  </Row>
                );
              })
            )}
          </>
        ) : null}
        {needle && landing.length === 0 && !hasSite ? <Note text="No pages match your search." tag="span" /> : null}
      </Scroll>
    </>
  );
};

export default PageList;
